import { message } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import { getMyEmployeeInfo } from '../../../api/aopsHR.api'
import AopsSimpleHeader from '../../../components/apos/AopsSimpleHeader'
import { userState } from '../../../store/user'
import AnnualLeaveSection from './components/AnnualLeaveSection'
import CertificateViewerModal from './components/CertificateViewerModal'
import ContractSection from './components/ContractSection'
import ContractSignModal from './components/ContractSignModal'
import MyInfoSection from './components/MyInfoSection'
import './myHrManage.scss'

const MyHrManagePage = () => {
  const [user, setUser] = useRecoilState(userState)
  const [personalInfo, setPersonalInfo] = useState(null)
  const [employmentInfo, setEmploymentInfo] = useState(null)
  const [contracts, setContracts] = useState([])
  const [selectedContract, setSelectedContract] = useState(null)
  const [isSignModalOpen, setIsSignModalOpen] = useState(false)
  const [selectedCertificate, setSelectedCertificate] = useState(null)
  const [isCertificateViewerOpen, setIsCertificateViewerOpen] = useState(false)

  // 주민등록번호(마스킹 포함)에서 생년월일 추출
  const parseBirthDateFromRRN = (maskingResidentRegistrationNumber) => {
    if (!maskingResidentRegistrationNumber) return null

    // 숫자만 추출 (예: "010209-3******" -> "0102093")
    const digits = maskingResidentRegistrationNumber.replace(/\D/g, '')
    if (digits.length < 7) return null

    const yy = digits.slice(0, 2)
    const mm = digits.slice(2, 4)
    const dd = digits.slice(4, 6)
    const genderCode = digits[6]

    let century = null
    if (genderCode === '1' || genderCode === '2') {
      century = 1900
    } else if (genderCode === '3' || genderCode === '4') {
      century = 2000
    } else {
      // 그 외의 경우는 현재 연도를 기준으로 보수적으로 계산
      const currentYearYY = Number(dayjs().format('YY'))
      century = Number(yy) > currentYearYY ? 1900 : 2000
    }

    const year = century + Number(yy)
    const dateString = `${year}-${mm}-${dd}`
    const parsed = dayjs(dateString, 'YYYY-MM-DD', true)

    return parsed.isValid() ? parsed : null
  }

  // 연락처 포맷팅 함수 (01072251125 -> 010-7225-1125)
  const formatPhoneNumber = (phone) => {
    if (!phone) return ''
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 11) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`
    }
    return phone
  }

  const handleViewContract = (contract) => {
    if (contract.itemType === 'CERTIFICATE') {
      setSelectedCertificate(contract)
      setIsCertificateViewerOpen(true)
      return
    }
    if (contract.status === '진행중') {
      setSelectedContract(contract)
      setIsSignModalOpen(true)
    } else {
      setSelectedContract(contract)
      setIsSignModalOpen(true)
    }
  }

  const handleCloseSignModal = () => {
    setIsSignModalOpen(false)
    setSelectedContract(null)
  }

  const getEmployeeData = async () => {
    try {
      const response = await getMyEmployeeInfo(user?.id)
      // console.log('getMyEmployeeInfo response::', response)
      const data = response?.data

      if (data) {
        // employeeId를 리코일에 저장
        if (data.employeeId) {
          setUser((prev) => ({
            ...prev,
            employeeId: data.employeeId,
          }))
        }

        // personalInfo 매핑
        setPersonalInfo({
          name: data.name || '-',
          // API에서 생년월일이 따로 내려오면 우선 사용, 없으면 주민등록번호에서 계산
          birthDate:
            data.birthDate ||
            parseBirthDateFromRRN(data.maskingResidentRegistrationNumber),
          phone: formatPhoneNumber(data.contact || '-'),
          email: data.email || '-',
          address: data.address || '-',
          employeeId: data.employeeId,
        })

        // employmentInfo 매핑
        setEmploymentInfo({
          status: data.employmentStatusDisplayName || '',
          team: data.teamName || '',
          position: data.position || '',
          employmentType: data.workTypeDisplayName || '',
          // 날짜 값이 없으면 null로 두고, 화면에서 안전하게 포맷
          joinDate: data.joinDate || null,
          leaveDate: data.resignationDate || null,
        })

        // contracts 매핑 (API 응답에 contracts 배열이 있는 경우)
        if (
          data.contracts &&
          Array.isArray(data.contracts) &&
          data.contracts.length > 0
        ) {
          const mappedContracts = data.contracts.map((contract, index) => {
            // 서버 상태값(status)을 화면용 한글 상태로 변환
            let displayStatus = ''
            if (contract.status === 'PENDING') {
              displayStatus = '진행중'
            } else if (contract.status === 'COMPLETED') {
              displayStatus = '완료'
            } else {
              displayStatus =
                contract.statusDisplayName || contract.status || '알수없음'
            }

            // 날짜 포맷팅 (요청일 / 기한)
            const requestDate = contract.createdAt
              ? dayjs(contract.createdAt).format('YYYY.MM.DD')
              : '-'
            const deadline = contract.contractEndDate
              ? dayjs(contract.contractEndDate).format('YYYY.MM.DD')
              : '-'

            // contractType에 따라 계약서 이름 결정
            let contractName = contract.contractName
            if (!contractName) {
              if (contract.contractType === 'SALARY') {
                contractName = '연봉 계약서'
              } else if (contract.contractType === 'EMPLOYMENT') {
                contractName = '근로 계약서'
              } else if (contract.itemType === 'CERTIFICATE') {
                contractName = '재직증명서'
              } else {
                contractName =
                  contract.contractTypeDisplayName || '표준 근로계약서'
              }
            }

            return {
              // 원본 데이터도 모두 유지
              ...contract,
              key: contract.id || index,
              status: displayStatus,
              requestDate,
              deadline,
              contractName,
            }
          })

          setContracts(mappedContracts)
        }
      }
    } catch (error) {
      console.error('error::', error)
      throw error
    }
  }

  useEffect(() => {
    if (user?.id) {
      getEmployeeData()
    }
  }, [user?.id])

  return (
    <div>
      <AopsSimpleHeader title="인사 관리" />

      <div className="my-hr-content-container">
        {/* 전자 곙약서 관리 */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '16px',
            justifyContent: 'space-between',
            marginBottom: '48px',
            marginTop: '24px',
          }}
        >
          <ContractSection
            contracts={contracts}
            onViewContract={handleViewContract}
          />

          {/* 연차 관리 */}
          <AnnualLeaveSection />
        </div>
        {/* 내 인사 정보 */}
        <MyInfoSection
          personalInfo={personalInfo}
          employmentInfo={employmentInfo}
        />
      </div>

      <ContractSignModal
        open={isSignModalOpen}
        onClose={handleCloseSignModal}
        contract={selectedContract}
        userId={user?.id}
        onSignComplete={getEmployeeData}
        mode={selectedContract?.status === '완료' ? 'view' : 'sign'}
      />

      <CertificateViewerModal
        open={isCertificateViewerOpen}
        onClose={() => {
          setIsCertificateViewerOpen(false)
          setSelectedCertificate(null)
        }}
        contract={selectedCertificate}
        personalInfo={personalInfo}
      />
    </div>
  )
}

export default MyHrManagePage
